<script setup lang="ts">
import { useId } from 'vue'

const {
  content,
  tag = 'span',
  focusable = true
} = defineProps<{
  content: string | false
  tag?: string
  /**
   * Set to false when the slot content is already focusable (eg. a button or
   * link), so it doesn't get a redundant, empty tab stop of its own.
   */
  focusable?: boolean
}>()

const id = useId()
</script>

<template>
  <component
    :is="tag"
    v-if="content"
    :data-tooltip="content"
    :aria-describedby="id"
    :tabindex="focusable ? 0 : undefined"
    :class="$style.tooltip"
  >
    <slot>
      <Icon icon="bi:question-circle" />
    </slot>
    <span
      :id
      class="visually-hidden"
    >{{ content }}</span>
  </component>
  <slot v-else />
</template>

<style module>
.tooltip {
  position: relative;
  cursor: help;

  /* Neutral by default. A color-* class on this same element (eg. color-destructive)
     overrides these two below, so the popup picks up that color instead. */
  --tooltip-popup-background: var(--tooltip-background);
  --tooltip-popup-color: var(--tooltip-color);

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    inset-block-end: 125%;
    inset-inline-start: 50%;
    transform: translateX(-50%);
    padding: 1rem;
    border-radius: 6px;
    white-space: normal;
    width: 200px;
    max-width: 200px;
    z-index: 999;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
    background-color: var(--tooltip-popup-background);
    color: var(--tooltip-popup-color);
    font-size: 0.875rem;
    line-height: 1.4;
  }

  &:hover::after,
  &:focus-within::after {
    opacity: 1;
  }
}

[class*="color-"].tooltip {
  --tooltip-popup-background: var(--color-200);
  --tooltip-popup-color: var(--color-foreground-850);
}

label .tooltip {
  margin-left: 1em;
}
</style>
